import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { retrieveAutomobileVersionsBySeriesREST } from '../legacy/services/apiRouterService';
import { stringify } from 'query-string';
import { useDebouncedCallback } from 'use-debounce/lib';
import { mq } from '../utils/styledComponentsUtils';

// Components
import VehicleMobileList from './VehicleMobileList';
import {
  SearchInput,
  LoadingSpinner,
  Alert,
  IconActionChevronLeft,
  IconActionChevronRight,
} from 'wg-fe-ui';
import VehicleLookupOrderTableHead from './VehicleLookupOrderTableHead';
import VehicleLookupOrderTableList from './VehicleLookupOrderTableList';
import SearchSelectInputV2 from './SearchSelectInputV2';

// Types
import { Brand, Model } from '../../../../types/SMT';
import { ReturnOptionProps } from '../../../../types/ProductFactory';
import { compareProps } from '../../../tarifications/src/legacy/services/proFlowService';
import { useBreakpointValue } from '@wegroup/design-system';

export interface VersionsProps {
  id: string;
  kw: number;
  version: string;
  brand: string;
  model: string;
  modelYear: string;
  price: number;
  cc: number;
  co2: number;
}

interface Versions {
  id: string;
  kw: number;
  version: string;
  brand: string;
  model: string;
  model_year: string;
  price: number;
  cc: number;
  co2: number;
}

interface Props {
  mobile: boolean;
  selected?: string;
  dataTestId: string;
  handleSelectedCar: (b: string, m: string, v: string) => void;
  selectedValues: {
    brand?: Brand;
    model?: Model;
    fuelType?: string;
    kw?: number;
  };
  categories?: Array<'PASSENGER_CAR' | 'LIGHT_COMMERCIAL_VEHICLE'>;
}

interface PaginationProps {
  count: number;
  next: string;
  current: number;
  previous: string;
  requested_pagelen: number;
  pagelen: number;
  msg: string;
  pages: number;
}

const VehicleLookupOrderTable: React.FC<React.PropsWithChildren<Props>> = ({
  selected,
  handleSelectedCar,
  dataTestId,
  selectedValues,
  mobile,
  categories,
}) => {
  const tableRef: React.MutableRefObject<HTMLUListElement | null> =
    useRef(null);
  const initialRender = useRef(true);

  const { t } = useTranslation();
  const [currentScrollPosition, setCurrentScrollPosition] = useState<number>();
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [pagination, setPagination] = useState<PaginationProps>();
  const [apiError, setApiError] = useState<string>();
  const [noResults, setNoResults] = useState(false);
  const [versions, setVersions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isAppending, setIsAppending] = useState(false);
  const [sortedBy, setSortedBy] = useState<{ [k: string]: string }>();

  const isMobile = useBreakpointValue({ base: true, tablet: false });

  useEffect(() => {
    setSearchValue('');
    setVersions([]);
    if (selectedValues?.brand && selectedValues?.model)
      getVersions(
        selectedValues?.brand?.key || '',
        selectedValues?.model,
        selectedValues?.fuelType,
        selectedValues?.kw,
      );
  }, [
    selectedValues.brand,
    selectedValues.fuelType,
    selectedValues.kw,
    selectedValues.model,
  ]);

  useEffect(() => {
    if (
      selectedValues?.brand &&
      selectedValues?.model &&
      !initialRender.current
    )
      getVersions(
        selectedValues?.brand?.key || '',
        selectedValues?.model,
        selectedValues?.fuelType,
        selectedValues?.kw,
      );
    else initialRender.current = false;
  }, [mobile]);

  const getPageNumberfromString = (urlString: string) => {
    const url = new URL(urlString);
    const urlParams = new URLSearchParams(url.search);
    return urlParams.get('page');
  };

  const getVersions = async (
    _brand: string,
    _model?: Model,
    _fuel?: string,
    _kw?: number,
    _sort?: { [p: string]: string },
    append?: boolean,
    prev?: boolean,
    query?: string,
    manualSearch?: boolean,
  ) => {
    !manualSearch && !append && setIsLoadingVersions(true);
    const queryObject = {
      category: categories,
      motor_type: _fuel === 'HYB' ? ['ELE_PET', 'ELE_DIE'] : _fuel,
      kw: _kw,
      q: query,
      ...(pagination?.next &&
        append && { page: getPageNumberfromString(pagination.next) }),
      ...(pagination?.previous &&
        prev && { page: getPageNumberfromString(pagination.previous) }),
      ...(_sort && {
        sort: `car.${_sort?.key === 'modelYear' ? 'model_year' : _sort?.key}:${
          _sort?.type
        }`,
      }),
      pagelen: isMobile ? 10 : 200,
    };

    const [resp, status] = await retrieveAutomobileVersionsBySeriesREST(
      _model?.id,
      stringify(queryObject, { skipEmptyString: true, skipNull: true }),
    );

    if (status !== 200 || !resp) {
      setApiError(
        resp?.msg ||
          t(
            'An error occured while trying to retrieve the car models Please try again',
          ),
      );
      return setIsLoadingVersions(false);
    }
    setApiError('');

    const { items, pagination: _pagination } = resp;

    setPagination(_pagination);

    const computedVersions = items.map(
      ({ version }: { version: Versions }) => ({
        id: version.id,
        kw: version.kw,
        version: version.version,
        brand: selectedValues?.brand?.label || _brand,
        model: _model?.model,
        modelYear: version.model_year,
        price: version.price,
        cc: version.cc,
        co2: version.co2,
      }),
    );

    if (!append && computedVersions.length === 0) {
      setNoResults(true);
    } else {
      setNoResults(false);
    }

    if (append && !mobile) {
      setVersions((versions) =>
        versions.concat(computedVersions.length > 0 ? computedVersions : []),
      );
    } else {
      setVersions(computedVersions.length > 0 ? computedVersions : null);
    }
    setIsLoadingVersions(false);
    setSearchLoading(false);
    setIsAppending(false);
  };
  const [debounceVersions] = useDebouncedCallback(getVersions, 250);

  const sortVersions = (key: string, _versions: VersionsProps[]) => {
    if (_versions?.length === 0 || !_versions) return;

    const type = mobile
      ? 'desc'
      : sortedBy?.type === 'asc' && sortedBy?.key === key
      ? 'desc'
      : 'asc';

    const _sortedBy = { key, type };
    setSearchLoading(true);
    setSortedBy({ key, type });
    getVersions(
      selectedValues?.brand?.key || '',
      selectedValues?.model,
      selectedValues?.fuelType || '',
      selectedValues?.kw,
      _sortedBy,
      false,
      false,
      searchValue,
      true,
    );
  };

  const handleSearch = (val: string) => {
    setSearchValue(val);
    setSearchLoading(true);
    debounceVersions(
      selectedValues?.brand?.key || '',
      selectedValues?.model,
      selectedValues?.fuelType || '',
      selectedValues?.kw,
      sortedBy,
      false,
      false,
      val,
      true,
    );
  };

  /** Used for versions table scroll to check if it's at bottom */
  const onScroll = useCallback(() => {
    if (
      !isAppending &&
      tableRef.current &&
      tableRef.current?.scrollTop ===
        tableRef.current.scrollHeight - tableRef.current.offsetHeight
    ) {
      if (pagination?.next) {
        setCurrentScrollPosition(tableRef?.current?.scrollTop);
        setIsAppending(true);
        getVersions(
          selectedValues?.brand?.key || '',
          selectedValues?.model,
          selectedValues?.fuelType || '',
          selectedValues?.kw,
          sortedBy,
          true,
          false,
          searchValue,
        );
      }
    }
  }, [
    isAppending,
    tableRef,
    pagination,
    selectedValues,
    sortedBy,
    searchValue,
  ]);

  const nextPage = () => {
    setIsAppending(true);
    getVersions(
      selectedValues?.brand?.key || '',
      selectedValues?.model,
      selectedValues?.fuelType || '',
      selectedValues?.kw,
      sortedBy,
      true,
      false,
      searchValue,
    );
  };

  const previousPage = () => {
    setIsAppending(true);
    getVersions(
      selectedValues?.brand?.key || '',
      selectedValues?.model,
      selectedValues?.fuelType || '',
      selectedValues?.kw,
      sortedBy,
      false,
      true,
      searchValue,
    );
  };

  useEffect(() => {
    if (
      versions?.length > 0 &&
      tableRef.current &&
      currentScrollPosition &&
      !mobile
    )
      tableRef.current.scrollTop = currentScrollPosition;
  }, [versions]);

  const sortOptions = [
    {
      label: t('kW'),
      value: 'kw',
    },
    {
      label: t('CC'),
      value: 'cc',
    },
    {
      label: t('Co2'),
      value: 'co2',
    },
    {
      label: t('Year'),
      value: 'modelYear',
    },
    {
      label: t('Price'),
      value: 'price',
    },
  ];

  return (
    <Container>
      {isLoadingVersions && (
        <LoadingWrapper>
          <LoadingSpinner />
        </LoadingWrapper>
      )}

      {apiError && (
        <StyledAlert icon type="error">
          {apiError}
        </StyledAlert>
      )}

      {selectedValues?.model && versions?.length !== 0 && !isLoadingVersions && (
        <>
          <StyledOrderTable onScroll={onScroll} data-test-id={dataTestId}>
            <SearchContainer>
              <StyledSearchInput
                text={searchValue}
                onChange={(val: string) => handleSearch(val)}
                onClear={() => handleSearch('')}
                placeholder={t('Search for a car')}
              />
            </SearchContainer>
            {!mobile && (
              <>
                <VehicleLookupOrderTableHead
                  sortVersions={sortVersions}
                  sortedBy={sortedBy}
                  versions={versions}
                />
                <VehicleLookupOrderTableList
                  ref={tableRef}
                  versions={versions}
                  fuel={selectedValues?.fuelType || ''}
                  model={selectedValues?.model}
                  selected={selected}
                  searchLoading={searchLoading}
                  noResults={noResults}
                  dataTestId={dataTestId}
                  isAppending={isAppending}
                  handleSelectedCar={handleSelectedCar}
                />
              </>
            )}
          </StyledOrderTable>
          {mobile && (
            <>
              <PageButtons>
                <PageButton
                  onClick={previousPage}
                  disabled={!pagination?.previous}
                >
                  <IconActionChevronLeft
                    color={
                      !pagination?.previous ? 'rgba(16, 16, 16, 0.3)' : 'black'
                    }
                  />
                  <p>{t('Previous')}</p>
                </PageButton>
                <PageInfo>
                  {pagination?.current} / {pagination?.pages}
                </PageInfo>
                <PageButton onClick={nextPage} disabled={!pagination?.next}>
                  <p>{t('Next')}</p>
                  <IconActionChevronRight
                    color={
                      !pagination?.next ? 'rgba(16, 16, 16, 0.3)' : 'black'
                    }
                  />
                </PageButton>
              </PageButtons>
              <StyledSearchSelectInputV2
                name="sortSelect"
                isClearable
                placeholder={t('SEARCH_OR_CHOOSE')}
                onSelected={({ value }: ReturnOptionProps) =>
                  sortVersions(value as string, versions)
                }
                options={sortOptions}
              >
                {t('Sort by')}
              </StyledSearchSelectInputV2>
              <VehicleMobileList
                versions={versions}
                selected={selected}
                searchLoading={searchLoading}
                noResults={noResults}
                isAppending={isAppending}
                handleSelectedCar={handleSelectedCar}
                model={selectedValues?.model}
              />
            </>
          )}
        </>
      )}
    </Container>
  );
};

const StyledSearchSelectInputV2 = styled(SearchSelectInputV2)`
  margin-bottom: 2.4rem;
`;

const PageInfo = styled.p`
  display: flex;
  align-items: center;
  text-align: center;
  font-size: 1.8rem;
  /* Fix for small icon offset */
  margin: 6px 0.8rem;
  margin-bottom: 0;
`;

const PageButton = styled.button`
  align-items: center;
  display: flex;
  border: none;
  background-color: transparent;

  p {
    font-size: 1.8rem;
    /* Fix for small icon offset */
    margin-top: 2px;
  }
`;

const PageButtons = styled.div`
  display: flex;
  justify-content: space-evenly;
  width: 100%;
  margin-bottom: 2.4rem;

  > &:first-child {
    margin-right: 0.8rem;
  }

  > &:last-child {
    margin-left: 0.8rem;
  }
`;

const Container = styled.div`
  width: 100%;
`;

const StyledOrderTable = styled.div`
  border-radius: 5px;
  background-color: white;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.gray[100]};
  margin: 3.2rem 0;

  > div:first-of-type {
    height: 5.5rem;
  }

  ${mq.tablet} {
    margin: 3.2rem 0 0;
  }
`;

const StyledAlert = styled(Alert)`
  margin-top: 2.4rem;
  margin-bottom: 2.4rem;
  > div {
    padding-top: 0;
    height: 100%;
    display: flex;
    align-items: center;
    font-size: 1.4rem;
  }
`;

const SearchContainer = styled.div`
  padding-left: 1.2rem;
  padding-right: 1.5rem;
  display: flex;
  align-items: center;
`;

const StyledSearchInput = styled(SearchInput)`
  color: #a29c95;
  height: 5.2rem;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 2rem 1rem;
  width: 100%;

  ${mq.tablet} {
    margin: 6rem 3rem;
  }
`;

export default React.memo(VehicleLookupOrderTable, compareProps);
